import moment from 'moment-timezone'
import { addAlert } from '@/utilities/helpers'
import { findSourceRecords, findAggregateRecords } from '@/services/reports-service'
import { findTransportationProfiles } from '@/services/transportation-profiles-service'

export default {
  name: 'distance-travelled-reports',
  props: {
    reportType: {
      type: String,
      default: 'distance_travelled'
    },
    vehicleIds: {
      type: Array,
      default: function () {
        return []
      }
    },
    dateFrom: {
      type: String,
      default: null
    },
    dateTo: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      loadingCharts: false,
      dayChart: {
        title: 'Distance per Day',
        categories: [],
        series: [],
        hasData: false
      },
      weekChart: {
        title: 'Distance per Week',
        categories: [],
        series: [],
        hasData: false
      },
      monthChart: {
        title: 'Distance per Month',
        categories: [],
        series: [],
        hasData: false
      },
      yearChart: {
        title: 'Distance per Year',
        categories: [],
        series: [],
        hasData: false
      },
      chartVisibility: {
        day: true,
        week: true,
        month: true,
        year: true
      },
      vehicleNameById: {},
      requestSequence: 0
    }
  },
  computed: {
    normalizedVehicleIds () {
      const ids = Array.isArray(this.vehicleIds) ? this.vehicleIds : []
      const uniqueMap = {}
      const normalized = []

      for (let i = 0; i < ids.length; i += 1) {
        const rawId = ids[i]

        if (rawId === null || rawId === undefined || String(rawId).trim() === '') {
          continue
        }

        const idKey = String(rawId)

        if (uniqueMap[idKey]) {
          continue
        }

        uniqueMap[idKey] = true
        normalized.push(rawId)
      }

      return normalized
    },
    hasCustomDateRange () {
      return !!(this.dateFrom && this.dateTo)
    },
    computedDateRangeDays () {
      if (!this.hasCustomDateRange) {
        return null
      }
      const start = moment.utc(this.dateFrom, 'YYYY-MM-DD', true)
      const end = moment.utc(this.dateTo, 'YYYY-MM-DD', true)
      if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
        return null
      }
      return end.diff(start, 'days') + 1
    },
    computedCanShowWeek () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 7
    },
    computedCanShowMonth () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 28
    },
    computedCanShowYear () {
      if (!this.hasCustomDateRange) {
        return true
      }
      return Number(this.computedDateRangeDays || 0) >= 365
    },
    fetchKey () {
      return JSON.stringify({
        reportType: this.reportType || null,
        vehicleIds: this.normalizedVehicleIds,
        dateFrom: this.dateFrom || null,
        dateTo: this.dateTo || null
      })
    }
  },
  watch: {
    fetchKey: {
      immediate: true,
      handler () {
        this.fetchReportData()
      }
    }
  },
  methods: {
    emptyChartState (title) {
      return {
        title: title,
        categories: [],
        series: [],
        hasData: false,
        reportRows: []
      }
    },
    resetChartsToEmptyState () {
      this.dayChart = this.emptyChartState('Distance per Day')
      this.weekChart = this.emptyChartState('Distance per Week')
      this.monthChart = this.emptyChartState('Distance per Month')
      this.yearChart = this.emptyChartState('Distance per Year')
    },
    async ensureVehicleNames (vehicleIds) {
      const missingIds = vehicleIds.filter((id) => id && !this.vehicleNameById[id])
      if (!missingIds.length) {
        return
      }

      const profiles = await findTransportationProfiles({
        where: { id: { inq: missingIds } },
        fields: { id: true, licensePlateNumber: true },
        limit: 10000
      })

      for (let i = 0; i < profiles.length; i += 1) {
        const profile = profiles[i]
        if (profile && profile.id) {
          this.vehicleNameById[profile.id] = profile.licensePlateNumber || profile.id
        }
      }

      for (let j = 0; j < missingIds.length; j += 1) {
        const id = missingIds[j]
        if (!this.vehicleNameById[id]) {
          this.vehicleNameById[id] = 'Vehicle ' + String(id).slice(0, 8)
        }
      }
    },
    vehicleName (vehicleId) {
      return this.vehicleNameById[vehicleId] || ('Vehicle ' + String(vehicleId).slice(0, 8))
    },
    flattenDataValue (value, prefix, output) {
      const target = output || {}
      const keyPrefix = prefix || ''

      if (value === null || value === undefined) {
        target[keyPrefix] = ''
        return target
      }

      if (Array.isArray(value)) {
        target[keyPrefix] = value
          .map((item) => {
            if (item === null || item === undefined) {
              return ''
            }
            return typeof item === 'object' ? JSON.stringify(item) : String(item)
          })
          .join(' | ')
        return target
      }

      if (typeof value === 'object') {
        const keys = Object.keys(value)
        for (let i = 0; i < keys.length; i += 1) {
          const childKey = keys[i]
          const childValue = value[childKey]
          const compoundKey = keyPrefix ? (keyPrefix + '.' + childKey) : childKey
          this.flattenDataValue(childValue, compoundKey, target)
        }
        return target
      }

      target[keyPrefix] = value
      return target
    },
    shouldExcludeDistanceCsvKey (key) {
      if (!key) {
        return false
      }
      return String(key).toLowerCase().includes('timestamp')
    },
    csvCell (value) {
      const normalizedValue = value === null || value === undefined ? '' : String(value)
      const escapedValue = normalizedValue.replace(/"/g, '""')
      return '"' + escapedValue + '"'
    },
    formatDistanceCsvValue (key, value) {
      if (!key) {
        return value
      }

      const normalizedKey = String(key).toLowerCase()
      if (!normalizedKey.includes('distance')) {
        return value
      }

      if (value === null || value === undefined || value === '') {
        return ''
      }

      const numericValue = Number(value)

      if (!Number.isFinite(numericValue)) {
        return value
      }

      return numericValue.toFixed(3)
    },
    rowsToCsv (columns, rows) {
      const safeColumns = Array.isArray(columns) ? columns : []
      const safeRows = Array.isArray(rows) ? rows : []
      const lines = []

      lines.push(safeColumns.map((column) => this.getCsvHeaderLabel(column)).join(','))
      for (let i = 0; i < safeRows.length; i += 1) {
        const row = safeRows[i] || {}
        const cells = safeColumns.map((column) => this.csvCell(row[column]))
        lines.push(cells.join(','))
      }

      return lines.join('\n')
    },
    buildReportRows (records, frequency) {
      const safeRecords = Array.isArray(records) ? records : []
      const rows = []

      for (let i = 0; i < safeRecords.length; i += 1) {
        const record = safeRecords[i] || {}
        const vehicleId = record.categoryId ? String(record.categoryId) : ''
        const periodStart = record.periodStart || ''
        const bucketKey = periodStart ? this.bucketKeyForDate(frequency, periodStart) : ''
        const flattenedData = this.flattenDataValue(record.data || {}, '', {})
        const row = {
          frequency: frequency,
          periodStart: periodStart,
          bucketKey: bucketKey,
          bucketLabel: periodStart ? this.bucketLabelForDate(frequency, periodStart) : '',
          categoryId: vehicleId,
          vehicleName: vehicleId ? this.vehicleName(vehicleId) : ''
        }
        const dataKeys = Object.keys(flattenedData)
        for (let j = 0; j < dataKeys.length; j += 1) {
          const key = dataKeys[j]
          if (this.shouldExcludeDistanceCsvKey(key)) {
            continue
          }
          row['data.' + key] = this.formatDistanceCsvValue(key, flattenedData[key])
        }
        rows.push(row)
      }

      return rows
    },
    shouldExcludeDistanceCsvColumn (columnKey) {
      if (!columnKey) {
        return false
      }

      const normalizedKey = String(columnKey).toLowerCase()
      const excludedColumns = {
        bucketkey: true,
        bucketlabel: true,
        categoryid: true
      }

      return !!excludedColumns[normalizedKey]
    },
    getCsvHeaderLabel (columnKey) {
      if (!columnKey) {
        return ''
      }

      const key = String(columnKey)
      const headerKey = key.startsWith('data.') ? key.slice(5) : key

      if (headerKey === 'distance') {
        return 'distance-km'
      }

      return headerKey
    },
    getChartCsvColumns (rows) {
      const baseColumns = [
        'frequency',
        'periodStart',
        'vehicleName'
      ]
      const extraColumnsMap = {}
      const safeRows = Array.isArray(rows) ? rows : []

      for (let i = 0; i < safeRows.length; i += 1) {
        const row = safeRows[i] || {}
        const rowKeys = Object.keys(row)
        for (let j = 0; j < rowKeys.length; j += 1) {
          const key = rowKeys[j]

          if (this.shouldExcludeDistanceCsvColumn(key)) {
            continue
          }

          if (baseColumns.includes(key)) {
            continue
          }

          extraColumnsMap[key] = true
        }
      }

      return baseColumns.concat(Object.keys(extraColumnsMap).sort())
    },
    csvSafeTitle (title) {
      return String(title || 'report')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },
    buildChartCsvFileName (chartState, frequency) {
      const reportWindowStart = this.dateFrom || moment.utc().format('YYYY-MM-DD')
      const reportWindowEnd = this.dateTo || reportWindowStart
      const chartName = this.csvSafeTitle(chartState && chartState.title ? chartState.title : (frequency + '-report'))
      return chartName + '-' + reportWindowStart + '-to-' + reportWindowEnd + '.csv'
    },
    async downloadChartCsv (chartState, frequency) {
      const rows = chartState && Array.isArray(chartState.reportRows) ? chartState.reportRows : []
      if (!rows.length) {
        throw new Error('No chart data available to download.')
      }

      const columns = this.getChartCsvColumns(rows)
      return this.rowsToCsv(columns, rows)
    },
    downloadDayChartCsv () {
      return this.downloadChartCsv(this.dayChart, 'day')
    },
    downloadWeekChartCsv () {
      return this.downloadChartCsv(this.weekChart, 'week')
    },
    downloadMonthChartCsv () {
      return this.downloadChartCsv(this.monthChart, 'month')
    },
    downloadYearChartCsv () {
      return this.downloadChartCsv(this.yearChart, 'year')
    },
    async fetchReportData () {
      const requestId = this.requestSequence + 1
      this.requestSequence = requestId

      const reportType = this.reportType || 'distance_travelled'
      const vehicleIds = this.normalizedVehicleIds

      if (!reportType || !vehicleIds.length) {
        this.resetChartsToEmptyState()
        return
      }

      this.loadingCharts = true
      try {
        await this.ensureVehicleNames(vehicleIds)
        if (requestId !== this.requestSequence) {
          return
        }

        this.chartVisibility = {
          day: true,
          week: this.computedCanShowWeek,
          month: this.computedCanShowMonth,
          year: this.computedCanShowYear
        }

        const jobs = []
        jobs.push(this.buildChartForFrequency('day', vehicleIds, reportType))
        jobs.push(this.chartVisibility.week ? this.buildChartForFrequency('week', vehicleIds, reportType) : Promise.resolve(this.emptyChartState('Distance per Week')))
        jobs.push(this.chartVisibility.month ? this.buildChartForFrequency('month', vehicleIds, reportType) : Promise.resolve(this.emptyChartState('Distance per Month')))
        jobs.push(this.chartVisibility.year ? this.buildChartForFrequency('year', vehicleIds, reportType) : Promise.resolve(this.emptyChartState('Distance per Year')))

        const [dayChart, weekChart, monthChart, yearChart] = await Promise.all(jobs)
        if (requestId !== this.requestSequence) {
          return
        }

        this.dayChart = dayChart
        this.weekChart = weekChart
        this.monthChart = monthChart
        this.yearChart = yearChart
      } catch (err) {
        if (requestId === this.requestSequence) {
          addAlert({
            message: err,
            type: 'error'
          })
        }
      } finally {
        if (requestId === this.requestSequence) {
          this.loadingCharts = false
        }
      }
    },
    resolveRangeForFrequency (frequency) {
      if (this.hasCustomDateRange) {
        const rangeStart = moment.utc(this.dateFrom, 'YYYY-MM-DD').startOf('day')
        const rangeEndExclusive = moment.utc(this.dateTo, 'YYYY-MM-DD').startOf('day').add(1, 'day')
        return { rangeStart: rangeStart, rangeEndExclusive: rangeEndExclusive }
      }

      if (frequency === 'day') {
        return {
          rangeStart: moment.utc().startOf('day').subtract(6, 'day'),
          rangeEndExclusive: moment.utc().startOf('day').add(1, 'day')
        }
      }

      if (frequency === 'week') {
        return {
          rangeStart: moment.utc().startOf('isoWeek').subtract(1, 'week'),
          rangeEndExclusive: moment.utc().startOf('isoWeek')
        }
      }

      if (frequency === 'month') {
        return {
          rangeStart: moment.utc().startOf('month').subtract(1, 'month'),
          rangeEndExclusive: moment.utc().startOf('month')
        }
      }

      return {
        rangeStart: moment.utc().startOf('year').subtract(1, 'year'),
        rangeEndExclusive: moment.utc().startOf('year')
      }
    },
    resolveApiPeriodBounds () {
      const defaultStart = moment.utc().startOf('day')
      const defaultEnd = moment.utc().hour(23).minute(59).second(0).millisecond(0)

      const start = this.dateFrom
        ? moment.utc(this.dateFrom, 'YYYY-MM-DD', true).startOf('day')
        : defaultStart.clone()
      const end = this.dateTo
        // Report records are stored with periodEnd at the next interval boundary,
        // so dateTo should include the start of the following day.
        ? moment.utc(this.dateTo, 'YYYY-MM-DD', true).startOf('day').add(1, 'day')
        : defaultEnd.clone()

      const safeStart = start.isValid() ? start : defaultStart.clone()
      let safeEnd = end.isValid() ? end : defaultEnd.clone()

      if (safeEnd.isBefore(safeStart)) {
        safeEnd = safeStart.clone().add(1, 'day')
      }

      return {
        periodStart: safeStart.toISOString(),
        periodEnd: safeEnd.toISOString()
      }
    },
    async buildChartForFrequency (frequency, vehicleIds, reportType) {
      const chartTitleByFrequency = {
        day: 'Distance per Day',
        week: 'Distance per Week',
        month: 'Distance per Month',
        year: 'Distance per Year'
      }

      const { rangeStart, rangeEndExclusive } = this.resolveRangeForFrequency(frequency)
      const apiPeriod = this.resolveApiPeriodBounds()
      const scopedVehicleIds = Array.isArray(vehicleIds) ? vehicleIds : []

      if (!scopedVehicleIds.length) {
        return this.emptyChartState(chartTitleByFrequency[frequency])
      }

      const where = {
        reportType: reportType,
        reportStatus: 'completed',
        category: 'transportation',
        categoryId: { inq: scopedVehicleIds },
        periodStart: { gte: apiPeriod.periodStart },
        periodEnd: { lte: apiPeriod.periodEnd }
      }

      let records = []
      if (frequency === 'day') {
        records = await findSourceRecords({
          where: where,
          fields: { categoryId: true, periodStart: true, data: true },
          limit: 10000
        })
      } else {
        where.reportFidelity = frequency
        records = await findAggregateRecords({
          where: where,
          fields: { categoryId: true, periodStart: true, data: true },
          limit: 10000
        })
      }

      const buckets = this.buildBuckets(frequency, rangeStart, rangeEndExclusive)
      const distanceByVehicleByBucket = {}

      for (let i = 0; i < scopedVehicleIds.length; i += 1) {
        distanceByVehicleByBucket[scopedVehicleIds[i]] = {}
      }

      for (let i = 0; i < records.length; i += 1) {
        const record = records[i]
        const vehicleId = record && record.categoryId ? String(record.categoryId) : null
        if (!vehicleId || !distanceByVehicleByBucket[vehicleId]) {
          continue
        }
        const bucketKey = this.bucketKeyForDate(frequency, record.periodStart)
        if (!buckets.find((item) => item.key === bucketKey)) {
          continue
        }
        const distance = Number(record && record.data && record.data.distance ? record.data.distance : 0)
        if (!distanceByVehicleByBucket[vehicleId][bucketKey]) {
          distanceByVehicleByBucket[vehicleId][bucketKey] = 0
        }
        distanceByVehicleByBucket[vehicleId][bucketKey] += Number.isFinite(distance) ? distance : 0
      }

      const series = scopedVehicleIds.map((vehicleId) => {
        const data = buckets.map((bucket) => {
          const value = distanceByVehicleByBucket[vehicleId] && distanceByVehicleByBucket[vehicleId][bucket.key]
            ? distanceByVehicleByBucket[vehicleId][bucket.key]
            : 0
          return Number(value.toFixed(3))
        })

        return {
          name: this.vehicleName(vehicleId),
          data: data
        }
      })

      const hasData = series.some((item) => item.data.some((value) => value > 0))

      return {
        title: chartTitleByFrequency[frequency],
        categories: buckets.map((item) => item.label),
        series: series,
        hasData: hasData,
        reportRows: this.buildReportRows(records, frequency)
      }
    },
    buildBuckets (frequency, rangeStart, rangeEndExclusive) {
      const buckets = []

      if (frequency === 'day') {
        let cursor = rangeStart.clone()
        while (cursor.isBefore(rangeEndExclusive)) {
          buckets.push({
            key: cursor.format('YYYY-MM-DD'),
            label: cursor.format('ddd YYYY-MM-DD')
          })
          cursor = cursor.add(1, 'day')
        }
        return buckets
      }

      const unitByFrequency = {
        week: 'week',
        month: 'month',
        year: 'year'
      }
      const startMethodByFrequency = {
        week: 'isoWeek',
        month: 'month',
        year: 'year'
      }

      const unit = unitByFrequency[frequency]
      const startMethod = startMethodByFrequency[frequency]
      let cursor = rangeStart.clone().startOf(startMethod)

      while (cursor.isBefore(rangeEndExclusive)) {
        const bucketStart = cursor.clone()
        const bucketEnd = cursor.clone().add(1, unit)
        if (bucketStart.isSameOrAfter(rangeStart) && bucketEnd.isSameOrBefore(rangeEndExclusive)) {
          buckets.push({
            key: this.bucketKeyForDate(frequency, bucketStart.toISOString()),
            label: this.bucketLabelForDate(frequency, bucketStart.toISOString())
          })
        }
        cursor = cursor.add(1, unit)
      }

      return buckets
    },
    bucketKeyForDate (frequency, dateIso) {
      const date = moment.utc(dateIso)
      if (frequency === 'day') {
        return date.format('YYYY-MM-DD')
      }
      if (frequency === 'week') {
        return date.startOf('isoWeek').format('YYYY-MM-DD')
      }
      if (frequency === 'month') {
        return date.startOf('month').format('YYYY-MM')
      }
      return date.startOf('year').format('YYYY')
    },
    bucketLabelForDate (frequency, dateIso) {
      const date = moment.utc(dateIso)
      if (frequency === 'week') {
        const weekStart = date.clone().startOf('isoWeek')
        const weekEnd = weekStart.clone().add(6, 'day')
        return weekStart.format('YYYY-MM-DD') + ' to ' + weekEnd.format('YYYY-MM-DD')
      }
      if (frequency === 'month') {
        return date.format('MMM YYYY')
      }
      return date.format('YYYY')
    },
    chartOptions (chartState) {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false }
        },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%'
          }
        },
        xaxis: {
          categories: chartState.categories
        },
        yaxis: {
          title: {
            text: 'Distance (km)'
          }
        },
        tooltip: {
          y: {
            formatter: function (value) {
              return Number(value || 0).toFixed(3) + ' km'
            }
          }
        }
      }
    }
  }
}
